import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDb } from './DbProvider';
import { NoDraftModelError, SpendCapError, type DraftEvent } from '../ai/draft';
import { explainProviderError } from '../ai/explain';
import type { SceneBeat } from '../data/planRepository';
import { raisedStop, usd, type SpendMeter } from '../domain/spend';
import type { Verdict } from '../ai/verify';
import type { Finding } from '../domain/verify';

/**
 * Draft a beat, from inside the scene.
 *
 * Sits under the beats because that is the question it answers: this scene
 * has to do X — write X. One beat at a time ([D29](../../docs/10-decisions.md)),
 * streamed as it lands so the writer reads along and can stop it, and nothing
 * touches the scene until they accept. Accepting keeps the page as it was as a
 * draft first, so it is one restore away from undone.
 *
 * What it costs is said after, not guessed before: the provider's own token
 * counts against the profile's prices. The window the brief was fitted to is
 * the model's, from the profile — the first time the budget has a real number.
 *
 * After the draft, what the laws make of it ([doc 04](../../docs/04-laws-engine.md)
 * phase 2): the free checks and the repetition guard every time, the rubric
 * laws through the critique model when one is set — each finding with the
 * law, the words it points to, and the fix. Uncertain claims, where the model
 * could not point to the text, are shown as that and never as findings. None
 * of it blocks acceptance; that stays the writer's call.
 *
 * Under the controls, what today has cost so far against the project's caps
 * ([D17](../../docs/10-decisions.md)). Past the warning the line says so and
 * drafting continues; at the stop the drafter refuses before it sends
 * anything, and the refusal comes with the raise, one tap, because the stop
 * is a guard against a loop and not a judgement about the work.
 */

const LENGTHS = [
  { value: 150, text: 'short — about 150 words' },
  { value: 300, text: 'a beat — about 300 words' },
  { value: 500, text: 'long — about 500 words' },
];

type Done = Extract<DraftEvent, { kind: 'done' }>;

export function SceneDraft({ projectId, sceneId, onAccepted }: {
  projectId: string;
  sceneId: string;
  onAccepted: () => void;
}) {
  const db = useDb();
  const [beats, setBeats] = useState<SceneBeat[]>([]);
  const [beatId, setBeatId] = useState<string>('');
  const [words, setWords] = useState(300);
  const [text, setText] = useState('');
  const [done, setDone] = useState<Done | null>(null);
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [model, setModel] = useState<{ model: string; window: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; needsModel?: boolean; stopped?: SpendMeter } | null>(null);
  const [meter, setMeter] = useState<SpendMeter | null>(null);
  const [meterToken, setMeterToken] = useState(0);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => {
    if (db.state !== 'ready') return;
    let cancelled = false;
    void (async () => {
      const mine = await db.plan.beatsForScene(sceneId);
      if (!cancelled) setBeats(mine);
    })();
    return () => { cancelled = true; };
  }, [db, sceneId]);

  useEffect(() => {
    if (db.state !== 'ready') return;
    let cancelled = false;
    void (async () => {
      const now = await db.spend.meter(projectId);
      if (!cancelled) setMeter(now);
    })();
    return () => { cancelled = true; };
  }, [db, projectId, meterToken]);

  // A draft in flight belongs to the scene it was asked for.
  useEffect(() => () => abort.current?.abort(), []);

  const start = useCallback(async () => {
    if (db.state !== 'ready') return;
    const ctl = new AbortController();
    abort.current = ctl;
    setBusy(true);
    setText('');
    setDone(null);
    setVerdict(null);
    setNote(null);
    try {
      // The ban list and the seam read the prose as saved; the last sentence is
      // still behind the editor's debounce until this returns.
      await db.flushAll();
      for await (const ev of db.drafter.draft(
        { projectId, sceneId, beatId: beatId || null, targetWords: words }, ctl.signal)) {
        if (ev.kind === 'brief') setModel({ model: ev.model, window: ev.window });
        else if (ev.kind === 'text') setText((t) => t + ev.text);
        else if (ev.kind === 'verdict') setVerdict(ev.verdict);
        else setDone(ev);
      }
    } catch (e) {
      if (e instanceof NoDraftModelError) setNote({ text: e.message, needsModel: true });
      else if (e instanceof SpendCapError) setNote({ text: e.message, stopped: e.meter });
      else setNote({ text: explainProviderError(e) });
    } finally {
      setBusy(false);
      abort.current = null;
      setMeterToken((t) => t + 1);
    }
  }, [db, projectId, sceneId, beatId, words]);

  const raise = useCallback(async (from: SpendMeter) => {
    if (db.state !== 'ready') return;
    const stopUsd = raisedStop(from.caps.stopUsd);
    try {
      await db.spend.setCaps(projectId, { warnUsd: Math.min(from.caps.warnUsd, stopUsd), stopUsd });
      setNote({ text: `The stop for this project is now ${usd(stopUsd)} a day. Draft again when you are ready.` });
    } catch (e) {
      setNote({ text: (e as Error).message ?? String(e) });
    } finally {
      setMeterToken((t) => t + 1);
    }
  }, [db, projectId]);

  const accept = useCallback(async () => {
    if (db.state !== 'ready' || !done) return;
    setBusy(true);
    try {
      const { words: total } = await db.drafter.accept(done.runId, sceneId);
      setNote({ text: `Accepted. The scene is now ${total.toLocaleString()} words; what was there before is kept as a draft.` });
      setText('');
      setDone(null);
      setVerdict(null);
      onAccepted();
    } catch (e) {
      setNote({ text: (e as Error).message ?? String(e) });
    } finally {
      setBusy(false);
    }
  }, [db, done, sceneId, onAccepted]);

  if (db.state !== 'ready') return null;

  const statusLine = done && (
    done.status === 'ok' ? `${done.words} words`
      : done.status === 'truncated' ? `${done.words} words — the model ran out of room; the beat may be unfinished`
        : done.status === 'cancelled' ? `${done.words} words — stopped here`
          : done.status === 'refused' ? 'The provider declined to write this'
            : done.status
  );

  return (
    <section className="mt-4" data-testid="scene-draft">
      <h2 className="text-xs font-semibold uppercase tracking-wide opacity-50">Draft</h2>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select
          aria-label="Beat to draft"
          value={beatId}
          onChange={(e) => setBeatId(e.target.value)}
          disabled={busy}
          className="min-w-0 flex-1 rounded-lg border border-current/20 bg-transparent px-2 py-1.5 text-sm">
          <option value="">{beats.length ? 'the next beat, unnamed' : 'no beat on this scene — continue anyway'}</option>
          {beats.map((b) => <option key={b.beatId} value={b.beatId}>{b.arcName}: {b.title}</option>)}
        </select>
        <select
          aria-label="How much to write"
          value={words}
          onChange={(e) => setWords(Number(e.target.value))}
          disabled={busy}
          className="rounded-lg border border-current/20 bg-transparent px-2 py-1.5 text-sm">
          {LENGTHS.map((l) => <option key={l.value} value={l.value}>{l.text}</option>)}
        </select>
        {busy
          ? (
            <button onClick={() => abort.current?.abort()}
              className="rounded-lg border border-current/20 px-3 py-1.5 text-sm font-medium">
              Stop
            </button>
          ) : (
            <button
              onClick={() => void start()}
              disabled={busy}
              className="rounded-lg border border-current/20 bg-current/10 px-3 py-1.5 text-sm
                         font-medium disabled:opacity-50">
              Draft this beat
            </button>
          )}
      </div>

      {meter && (
        <p className="mt-1.5 text-xs opacity-60" data-testid="spend-meter" data-level={meter.level}>
          {usd(meter.todayUsd)} spent today on this project
          {meter.level === 'stop' ? ` — at the ${usd(meter.caps.stopUsd)} stop`
            : meter.level === 'warn' ? ` — past the ${usd(meter.caps.warnUsd)} warning, stops at ${usd(meter.caps.stopUsd)}`
              : ` · warns at ${usd(meter.caps.warnUsd)}, stops at ${usd(meter.caps.stopUsd)}`}
          {meter.unpricedRuns > 0 && (
            <> · {meter.unpricedRuns} {meter.unpricedRuns === 1 ? 'run' : 'runs'} had no price and {meter.unpricedRuns === 1 ? 'is' : 'are'} not counted</>
          )}
          {' '}
          <Link to={`/project/${projectId}/providers#spend`} className="underline">change the caps</Link>
        </p>
      )}

      {note && (
        <p aria-live="polite" role="status" className="mt-2 text-xs opacity-80">
          {note.text}
          {note.needsModel && (
            <>
              {' '}
              <Link to={`/project/${projectId}/providers`} className="underline">Settings →</Link>
            </>
          )}
          {note.stopped && (
            <>
              {' '}
              <button
                onClick={() => void raise(note.stopped!)}
                className="rounded border border-current/20 px-2 py-0.5 text-xs font-medium">
                Raise the stop to {usd(raisedStop(note.stopped.caps.stopUsd))} a day
              </button>
            </>
          )}
        </p>
      )}

      {(text || busy) && (
        <div
          data-testid="draft-text"
          aria-live="polite"
          className="mt-3 whitespace-pre-wrap rounded-lg border border-current/15 p-3 text-sm leading-relaxed">
          {text || <span className="opacity-50">Compiling the brief…</span>}
        </div>
      )}

      {done && verdict && <Findings verdict={verdict} projectId={projectId} />}

      {done && (
        <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs opacity-70" data-testid="draft-done">
          <span>{statusLine}</span>
          {model && <span>{model.model} · {(model.window / 1000).toFixed(0)}k window</span>}
          {done.tokensIn !== null && (
            <span>{done.tokensIn.toLocaleString()} in, {done.tokensOut?.toLocaleString()} out</span>
          )}
          {done.costUsd !== null && <span>${done.costUsd.toFixed(4)}</span>}
          {done.servedBy && <span>served by {done.servedBy}</span>}
          <span className="flex-1" />
          {done.output && (
            <button
              onClick={() => void accept()}
              disabled={busy}
              className="rounded-lg border border-current/20 bg-current/10 px-3 py-1 text-sm font-medium
                         disabled:opacity-50">
              Accept into the scene
            </button>
          )}
          <button
            onClick={() => { setText(''); setDone(null); setVerdict(null); }}
            disabled={busy}
            className="underline disabled:opacity-30">
            Discard
          </button>
        </div>
      )}
    </section>
  );
}

function rubricLine(v: Verdict): string | null {
  const r = v.rubric;
  const n = `${r.laws} rubric ${r.laws === 1 ? 'law' : 'laws'}`;
  switch (r.state) {
    case 'no-laws': return null;
    case 'ran': return `${n} checked by the critique model${r.costUsd !== null ? ` for ${usd(r.costUsd)}` : ''}.`;
    case 'no-model': return `${n} unchecked: no critique model is set.`;
    case 'no-key': return `${n} unchecked: ${r.detail ?? 'no key is saved for the critique model.'}`;
    case 'blocked': return `${n} unchecked: ${r.detail ?? 'the spend stop.'}`;
    case 'cancelled': return `${n} unchecked, because the draft was stopped.`;
    case 'refused': return `${n} unchecked: the critique model declined.`;
    case 'malformed': return `${n} unchecked: ${r.detail ?? 'the critique model did not answer in the shape asked for.'}`;
    case 'failed': return `${n} unchecked: ${r.detail ?? 'the check failed.'}`;
  }
}

function FindingLine({ f }: { f: Finding }) {
  return (
    <li data-finding={f.lawId} data-source={f.source} className="text-xs">
      <span className="rounded-full border border-current/25 px-1.5 opacity-70">{f.severity}</span>{' '}
      <span className="font-medium">{f.lawTitle}</span>
      {f.quote && <> — <q className="opacity-90">{f.quote}</q></>}
      <span className="opacity-70"> {f.explanation}</span>
      {f.suggestedFix && <span className="opacity-70"> Fix: {f.suggestedFix}</span>}
    </li>
  );
}

/** What the laws made of the draft. Informs the accept button; never disables it. */
function Findings({ verdict, projectId }: { verdict: Verdict; projectId: string }) {
  const { findings, uncertain, repetition, skipped, rubric } = verdict;
  const clean = findings.length === 0 && repetition.length === 0;
  const line = rubricLine(verdict);
  return (
    <div className="mt-2 rounded-lg border border-current/15 p-2.5" data-testid="draft-findings"
      data-findings={findings.length} data-uncertain={uncertain.length}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-50">
        {clean ? 'Checked: nothing to flag' : `Checked: ${findings.length + repetition.length} to look at`}
      </p>
      {findings.length > 0 && (
        <ul className="mt-1.5 space-y-1">{findings.map((f, i) => <FindingLine key={`${f.lawId}-${i}`} f={f} />)}</ul>
      )}
      {repetition.length > 0 && (
        <p className="mt-1.5 text-xs opacity-80" data-testid="draft-repetition">
          <span className="font-medium">Repeats what is already written:</span>{' '}
          {repetition.map((r) => (r.kind === 'opening'
            ? `opens like an earlier scene (“${r.value}”)`
            : `“${r.value}” ×${r.detail}`)).join('; ')}
        </p>
      )}
      {uncertain.length > 0 && (
        <div className="mt-1.5 text-xs opacity-60" data-testid="draft-uncertain">
          <p>
            The critique model also claimed {uncertain.length} more but could not point to the words,
            so {uncertain.length === 1 ? 'it is' : 'they are'} not counted:
          </p>
          <ul className="mt-0.5 list-disc pl-4">
            {uncertain.map((u, i) => (
              <li key={`${u.lawId}-u${i}`}>{u.lawTitle}: {u.explanation}{u.quote ? ` (claimed “${u.quote}”)` : ''}</li>
            ))}
          </ul>
        </div>
      )}
      {(line || skipped.length > 0) && (
        <p className="mt-1.5 text-xs opacity-60" data-testid="draft-rubric" data-state={rubric.state}>
          {line}
          {rubric.state === 'no-model' && (
            <>
              {' '}
              <Link to={`/project/${projectId}/providers`} className="underline">Settings →</Link>
            </>
          )}
          {skipped.length > 0 && (
            <> {skipped.length} {skipped.length === 1 ? 'check' : 'checks'} could not run: {skipped.map((s) => s.reason).join('; ')}.</>
          )}
        </p>
      )}
    </div>
  );
}
